import { useEffect, useMemo, useRef, useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';
import { COUNTRIES, flagEmoji } from '@/lib/iso/countries';

export interface MultiCountrySelectProps {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  // Cap how many chips render inline on the trigger before collapsing to "+N more".
  maxInlineChips?: number;
  // Optional cap on how many can be picked (e.g. plan-tier limit).
  max?: number;
  className?: string;
  ariaLabel?: string;
}

/**
 * Searchable multi-select country picker. Trigger shows selected
 * countries as removable chips; popover lists all 60+ markets with
 * checkable rows + search across name / code / aliases.
 */
export function MultiCountrySelect({
  value,
  onChange,
  placeholder,
  disabled,
  maxInlineChips = 3,
  max,
  className,
  ariaLabel,
}: MultiCountrySelectProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const valueSet = useMemo(() => new Set(value.map((v) => v.toUpperCase())), [value]);

  const filtered = useMemo(() => {
    if (!query) return COUNTRIES;
    const q = query.toLowerCase();
    return COUNTRIES.filter((c) => {
      const hay = [c.code, c.name, ...(c.aliases ?? [])]
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [query]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 30);
    else setQuery('');
  }, [open]);

  function toggle(code: string) {
    const c = code.toUpperCase();
    if (valueSet.has(c)) {
      onChange(value.filter((v) => v.toUpperCase() !== c));
    } else {
      if (max !== undefined && value.length >= max) return;
      onChange([...value, c]);
    }
  }

  function removeOne(code: string) {
    onChange(value.filter((v) => v.toUpperCase() !== code.toUpperCase()));
  }

  const selectedCountries = useMemo(
    () =>
      value
        .map((code) => COUNTRIES.find((c) => c.code === code.toUpperCase()))
        .filter((x): x is (typeof COUNTRIES)[number] => Boolean(x)),
    [value],
  );

  const inlineChips = selectedCountries.slice(0, maxInlineChips);
  const overflow = selectedCountries.length - inlineChips.length;
  const atCap = max !== undefined && value.length >= max;

  return (
    <Popover.Root open={open} onOpenChange={(o) => !disabled && setOpen(o)}>
      <Popover.Trigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-label={ariaLabel}
          className={cn(
            'flex w-full items-center justify-between gap-2 rounded-lg border border-ink-300 bg-white px-3 py-2 text-sm text-ink-900 min-h-[2.5rem]',
            'hover:border-ink-400 focus:outline-none focus:ring-2 focus:ring-accent-500/30 focus-visible:border-accent-500',
            'disabled:cursor-not-allowed disabled:opacity-60',
            'data-[state=open]:border-accent-500 data-[state=open]:ring-2 data-[state=open]:ring-accent-500/30',
            className,
          )}
        >
          <span className="flex flex-wrap items-center gap-1.5 min-w-0">
            {selectedCountries.length === 0 && (
              <span className="text-ink-500">
                {placeholder ?? t('common.select_placeholder')}
              </span>
            )}
            {inlineChips.map((c) => (
              <span
                key={c.code}
                className="inline-flex items-center gap-1 rounded-md bg-accent-50 px-2 py-0.5 text-xs text-accent-700"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  removeOne(c.code);
                }}
                role="button"
                tabIndex={-1}
              >
                <span aria-hidden role="img">{flagEmoji(c.code)}</span>
                <span>{c.name}</span>
                <X className="h-3 w-3 opacity-70 hover:opacity-100" />
              </span>
            ))}
            {overflow > 0 && (
              <span className="text-xs text-ink-500">+{overflow}</span>
            )}
          </span>
          <ChevronDown className="h-3.5 w-3.5 text-ink-500 shrink-0" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={4}
          className={cn(
            'z-50 rounded-lg border border-ink-200 bg-white shadow-lg',
            'data-[state=open]:animate-in data-[state=open]:fade-in data-[state=open]:zoom-in-95',
            'w-[var(--radix-popover-trigger-width)] min-w-[16rem]',
          )}
        >
          <div className="flex items-center gap-2 border-b border-ink-200 px-3 py-2">
            <Search className="h-3.5 w-3.5 text-ink-400 shrink-0" aria-hidden />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('common.search')}
              className="w-full bg-transparent text-sm text-ink-900 focus:outline-none placeholder:text-ink-400"
              autoComplete="off"
              spellCheck={false}
            />
            {value.length > 0 && (
              <button
                type="button"
                onClick={() => onChange([])}
                className="text-[11px] text-ink-500 hover:text-ink-900 px-1"
              >
                {t('common.clear')}
              </button>
            )}
          </div>
          <ul className="max-h-72 overflow-y-auto py-1" role="listbox" aria-multiselectable>
            {filtered.length === 0 && (
              <li className="px-3 py-2 text-xs text-ink-500">
                {t('common.no_results')}
              </li>
            )}
            {filtered.map((c) => {
              const selected = valueSet.has(c.code);
              const lockedOut = !selected && atCap;
              return (
                <li key={c.code} role="option" aria-selected={selected}>
                  <button
                    type="button"
                    disabled={lockedOut}
                    onClick={() => toggle(c.code)}
                    className={cn(
                      'flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm',
                      'hover:bg-ink-100 focus:outline-none focus:bg-ink-100',
                      'disabled:cursor-not-allowed disabled:opacity-50',
                      selected && 'bg-accent-50 text-accent-700',
                    )}
                  >
                    <span aria-hidden role="img" className="text-base leading-none w-5">
                      {flagEmoji(c.code)}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block truncate">{c.name}</span>
                      <span className="block truncate text-[11px] text-ink-500 num">
                        {c.code}
                      </span>
                    </span>
                    {selected && (
                      <Check className="h-3.5 w-3.5 text-accent-600 shrink-0" />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
          {max !== undefined && (
            <div className="border-t border-ink-200 px-3 py-1.5 text-[11px] text-ink-500">
              {value.length} / {max}
            </div>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
